import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SelectField, TextAreaField, TextField } from "@/components/field";
import type { Subscription } from "@/lib/types";
import { usePlans, usePutSubscription } from "./queries";

export const SUB_STATUSES = ["trialing", "active", "past_due", "cancelled"] as const;

const schema = z.object({
  plan_code: z.string().min(1, "Choose a plan"),
  seats: z.coerce.number().int().min(1, "At least one seat"),
  status: z.string().min(1),
  sub_until: z.string().min(1, "Required"),
  interval: z.enum(["month", "year", ""]),
  reason: z.string().trim().min(3, "Give a reason (audit log)"),
});
type FormValues = z.input<typeof schema>;
type Parsed = z.output<typeof schema>;

export function SubscriptionDialog({
  orgId,
  current,
  open,
  onOpenChange,
}: {
  orgId: string;
  current: Subscription | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const plans = usePlans();
  const put = usePutSubscription(orgId);
  const form = useForm<FormValues, unknown, Parsed>({
    resolver: zodResolver(schema),
    values: {
      plan_code: current?.plan_code ?? "starter",
      seats: current?.seats ?? 1,
      status: current?.status ?? "active",
      sub_until: current?.sub_until?.slice(0, 10) ?? "",
      interval: (current?.interval as "month" | "year" | null) ?? "",
      reason: "",
    },
  });
  const err = (k: keyof FormValues) => form.formState.errors[k]?.message;

  const onSubmit = form.handleSubmit(async (v) => {
    await put.mutateAsync({
      plan_code: v.plan_code,
      seats: v.seats,
      status: v.status,
      sub_until: v.sub_until,
      interval: v.interval || undefined,
      reason: v.reason,
    });
    form.reset();
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Change plan / seats</DialogTitle>
            <DialogDescription>
              Overrides the subscription for this organisation. Licences re-issue on next check-in.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Plan"
              error={err("plan_code")}
              hint={
                plans.data?.fallback ? "Billing plans endpoint unavailable; codes only." : undefined
              }
              {...form.register("plan_code")}
            >
              {(plans.data?.items ?? []).map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name}
                  {p.seats ? ` · ${p.seats} seats` : ""}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Seats"
              type="number"
              min={1}
              error={err("seats")}
              {...form.register("seats")}
            />
            <SelectField label="Status" error={err("status")} {...form.register("status")}>
              {SUB_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SelectField>
            <SelectField label="Interval" {...form.register("interval")}>
              <option value="">—</option>
              <option value="month">month</option>
              <option value="year">year</option>
            </SelectField>
            <TextField
              label="Subscribed until"
              type="date"
              error={err("sub_until")}
              className="sm:col-span-2"
              {...form.register("sub_until")}
            />
          </div>
          <TextAreaField
            label="Reason (required)"
            placeholder="Why is the plan changing? Recorded in the audit log."
            error={err("reason")}
            {...form.register("reason")}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={put.isPending}>
              {put.isPending ? "Saving…" : "Save subscription"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
